import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { ArrowRight, Brain, Beaker, MessagesSquare, Sparkles, BookOpen, Store } from "lucide-react";

export default function Dashboard() {
  const { user, authAxios } = useAuth();
  const { tr } = useLang();
  const [stats, setStats] = useState({ conversations: 0, messages: 0, plan: "Member" });
  const [academy, setAcademy] = useState({ total_lessons: 0, completed_lessons: 0, progress_percent: 0 });

  useEffect(() => {
    (async () => {
      try {
        const [c, s, a] = await Promise.all([
          authAxios.get("/conversations"),
          authAxios.get("/subscription"),
          authAxios.get("/academy/progress"),
        ]);
        const totalMessages = c.data.reduce((acc, x) => acc + (x.message_count || 0), 0);
        setStats({ conversations: c.data.length, messages: totalMessages, plan: s.data.plan || "Member" });
        setAcademy(a.data);
      } catch (e) {}
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const paths = [
    {
      testid: "dash-path-learn",
      pathNo: tr("path_learn_no"),
      eyebrow: tr("path_learn"),
      title: tr("path_learn_title"),
      sub: tr("path_learn_sub"),
      desc: tr("dash_path_learn_desc"),
      cta: tr("dash_path_learn_cta"),
      to: "/academy",
      icon: <BookOpen size={14} />,
      progress: { done: academy.completed_lessons, total: academy.total_lessons, percent: academy.progress_percent },
    },
    {
      testid: "dash-path-explore",
      pathNo: tr("path_explore_no"),
      eyebrow: tr("path_explore"),
      title: tr("path_explore_title"),
      sub: tr("path_explore_sub"),
      desc: tr("dash_path_explore_desc"),
      cta: tr("dash_path_explore_cta"),
      to: "/ai-perfumer",
      icon: <Brain size={14} />,
      accent: true,
    },
    {
      testid: "dash-path-create",
      pathNo: tr("path_create_no"),
      eyebrow: tr("path_create"),
      title: tr("path_create_title"),
      sub: tr("path_create_sub"),
      desc: tr("dash_path_create_desc"),
      cta: tr("dash_path_create_cta"),
      to: "/perfumery-tech-app",
      icon: <Beaker size={14} />,
      note: tr("path_create_note"),
    },
    {
      testid: "dash-path-craft",
      pathNo: tr("path_craft_no"),
      eyebrow: tr("path_craft"),
      title: tr("path_craft_title"),
      sub: tr("path_craft_sub"),
      desc: tr("dash_path_craft_desc"),
      cta: tr("dash_path_craft_cta"),
      to: "/twfh",
      icon: <Store size={14} />,
    },
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-10 py-10">
        {/* Greeting */}
        <div className="pfi-fade-up">
          <div className="pfi-overline">PERSONA FRAGRANCE INTELLIGENCE</div>
          <h1 className="mt-3 font-serif text-3xl sm:text-4xl">
            {tr("dash_hello")}, <span className="text-[#e6c687]">{user?.name || user?.email}</span>
          </h1>
          <p className="mt-2 text-[#a9a9b8]">{tr("dash_welcome")}</p>
        </div>

        {/* Four Ecosystem Paths */}
        <section className="mt-10">
          <div className="pfi-overline" data-testid="dash-paths-eyebrow">{tr("dash_paths_eyebrow")}</div>
          <h2 className="mt-3 font-serif text-2xl sm:text-3xl">{tr("dash_paths_title")}</h2>
          <p className="mt-3 text-sm text-[#a9a9b8] max-w-2xl">{tr("dash_paths_sub")}</p>
          <div className="mt-8 grid md:grid-cols-2 xl:grid-cols-4 gap-4">
            {paths.map((p) => <PathCard key={p.testid} p={p} tr={tr} />)}
          </div>
        </section>

        {/* Existing Activity Stats */}
        <section className="mt-14">
          <div className="pfi-overline">{tr("dash_your_activity")}</div>
          <div className="mt-4 grid sm:grid-cols-3 gap-4">
            <Stat label={tr("dash_stat_conversations")} value={stats.conversations} icon={<MessagesSquare size={16} />} />
            <Stat label={tr("dash_stat_messages")} value={stats.messages} icon={<Sparkles size={16} />} />
            <Stat label={tr("dash_stat_plan")} value={stats.plan} icon={<Brain size={16} />} />
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

function PathCard({ p, tr }) {
  return (
    <Link
      to={p.to}
      data-testid={p.testid}
      className={
        "pfi-card p-6 sm:p-7 flex flex-col h-full group " +
        (p.accent ? "border-[#d4af37]/40" : "")
      }
    >
      <div className="flex items-center justify-between">
        <div className="pfi-overline inline-flex items-center gap-2">{p.icon}{p.eyebrow}</div>
        <div className="pfi-overline text-[10px] text-[#6c6c7a]">{p.pathNo}</div>
      </div>
      <div className="mt-4 font-serif text-xl text-[#f8f8fc]">{p.title}</div>
      <div className="mt-1 text-[11px] font-mono tracking-wider text-[#a9a9b8] uppercase">{p.sub}</div>
      <p className="mt-4 text-sm text-[#a9a9b8] leading-relaxed flex-1">{p.desc}</p>

      {p.progress && p.progress.total > 0 ? (
        <div className="mt-4" data-testid={`${p.testid}-progress`}>
          <div className="flex items-center justify-between text-[11px] font-mono text-[#6c6c7a] mb-1.5">
            <span>{p.progress.done}/{p.progress.total} {tr("academy_lessons")}</span>
            <span className="text-[#e6c687]">{p.progress.percent}%</span>
          </div>
          <div className="h-1 rounded-full bg-white/5 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#e6c687] to-[#d4af37]"
              style={{ width: `${p.progress.percent}%` }}
            />
          </div>
        </div>
      ) : null}

      {p.note ? (
        <p className="mt-3 text-[11px] font-mono text-[#6c6c7a] leading-relaxed">{p.note}</p>
      ) : null}

      <div className="mt-6 inline-flex items-center gap-1 text-[#e6c687] text-xs group-hover:gap-2 transition-all">
        {p.cta} <ArrowRight size={12} />
      </div>
    </Link>
  );
}

function Stat({ label, value, icon }) {
  return (
    <div className="pfi-card p-5">
      <div className="flex items-center gap-2 pfi-overline">{icon}<span>{label}</span></div>
      <div className="mt-3 font-serif text-3xl text-[#f8f8fc]">{value}</div>
    </div>
  );
}
